/* GrowthOS — Emails tab (shared across all module view pages).
   Threaded, expandable email history ported from design growthos/Emails.jsx.
   Wired to the real per-record emails on `localData.emails`
   (fields: subject, message, fromAddress, toAddress, ccAddress, status,
   createdDateTime / scheduledDateTime).
   NOTE: the design's open/click engagement, reply-tracking, inbound thread and
   attachment chips are omitted — the per-record email backend exposes none of
   those. The stat strip therefore shows Total / Sent / Scheduled (all derivable
   from status). Compose / Reply / Forward / Edit reuse the real ComposeEmail form. */
import { useMemo, useState } from 'react';
import ComposeEmail from './compose';
import { DetailDateFormatter } from '../../../shared/controls/formatters';
import { Ic } from "../../../shared/component/GosIcon";


/* status → design tone (badge tint + label) */
const STATUS = {
  sent: { label: 'Sent', tone: 'green' },
  delivered: { label: 'Delivered', tone: 'blue' },
  opened: { label: 'Opened', tone: 'green' },
  scheduled: { label: 'Scheduled', tone: 'amber' },
  draft: { label: 'Draft', tone: 'gray' },
  failed: { label: 'Failed', tone: 'red' },
  received: { label: 'Received', tone: 'purple' },
};
const statusKey = (s) => {
  const v = String(s || '').toLowerCase();
  if (v.includes('schedul')) return 'scheduled';
  if (v.includes('fail') || v.includes('bounce')) return 'failed';
  if (v.includes('draft')) return 'draft';
  if (v.includes('receiv') || v.includes('repl')) return 'received';
  if (v.includes('open')) return 'opened';
  if (v.includes('sent') || v.includes('deliver')) return 'sent';
  return 'delivered';
};

const isHtml = (s) => /<[a-z][\s\S]*>/i.test(String(s || ''));
const stripHtml = (s) =>
  String(s || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
const initialsOf = (s) => {
  const base = String(s || '?').split('@')[0].replace(/[._-]+/g, ' ').trim();
  const parts = base.split(' ').filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
};

function Avatar({ seed, tone, size = 34 }) {
  return (
    <span className="gos-eml-avatar" style={{ width: size, height: size, background: tone, fontSize: size * 0.36 }}>
      {initialsOf(seed)}
    </span>
  );
}

function EmailRow({ mail, open, onToggle, onReply, onForward, onEdit }) {
  const key = statusKey(mail.status);
  const st = STATUS[key] || STATUS.delivered;
  const inbound = mail.dir === 'in';
  const senderTone = inbound ? 'var(--gos-purple-fg)' : 'var(--gos-accent)';
  const hasBody = !!(mail.message && stripHtml(mail.message));

  const addr = [
    ['From', mail.fromAddress],
    ['To', mail.toAddress],
    mail.ccAddress ? ['Cc', mail.ccAddress] : null,
    [key === 'scheduled' ? 'Sends' : 'Date', mail.when],
  ].filter(Boolean);

  return (
    <div className={`gos-eml-item${open ? ' open' : ''}`}>
      <button type="button" className="gos-eml-row" onClick={onToggle}>
        <span className="gos-eml-ava">
          <Avatar seed={mail.fromAddress} tone={senderTone} size={34} />
          <span className={`gos-eml-dir ${inbound ? 'in' : 'out'}`} title={inbound ? 'Received' : 'Sent'}>
            <Ic name={inbound ? 'arrowDown' : 'arrowUp'} size={10} />
          </span>
        </span>

        <span className="gos-eml-main">
          <span className="gos-eml-line1">
            <span className="gos-eml-subj">{mail.subject || '(no subject)'}</span>
          </span>
          <span className="gos-eml-snip">{mail.snippet || '—'}</span>
        </span>

        <span className="gos-eml-right">
          <span className={`gos-eml-badge ${st.tone}`}>
            <span className="gos-eml-pip" />
            {st.label}
          </span>
          <span className="gos-eml-when">{mail.when || '—'}</span>
          <span className="gos-eml-chev">
            <Ic name="chevD" size={15} />
          </span>
        </span>
      </button>

      {open && (
        <div className="gos-eml-detail">
          <div className="gos-eml-addr">
            {addr.map(([k, v]) => (
              <div className="gos-eml-addr-row" key={k}>
                <span className="gos-eml-addr-k">{k}</span>
                <span className={`gos-eml-addr-v${k === 'Date' || k === 'Sends' ? ' muted' : ''}`}>{v || '—'}</span>
              </div>
            ))}
          </div>

          {hasBody ? (
            isHtml(mail.message) ? (
              <div className="gos-eml-body" dangerouslySetInnerHTML={{ __html: mail.message }} />
            ) : (
              <div className="gos-eml-body">{mail.message}</div>
            )
          ) : (
            <div className="gos-eml-body muted">No content.</div>
          )}

          <div className="gos-eml-actions">
            <div />
            <div className="gos-eml-btn-row">
              {key === 'scheduled' ? (
                <button type="button" className="gos-eml-btn ghost" onClick={() => onEdit(mail)}>
                  <Ic name="edit" size={13} />
                  Edit
                </button>
              ) : (
                <>
                  <button type="button" className="gos-eml-btn ghost" onClick={() => onForward(mail)}>
                    <Ic name="forward" size={13} />
                    Forward
                  </button>
                  <button type="button" className="gos-eml-btn soft" onClick={() => onReply(mail)}>
                    <Ic name="reply" size={13} />
                    Reply
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Email = ({ localData, module }) => {
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [compose, setCompose] = useState(null);

  const mails = useMemo(() => {
    const list = Array.isArray(localData?.emails) ? localData.emails : [];
    return list.map((e, i) => {
      const key = statusKey(e.status);
      const rawDate = key === 'scheduled' ? e.scheduledDateTime || e.createdDateTime : e.createdDateTime;
      const when = rawDate ? DetailDateFormatter(rawDate) : '';
      return {
        ...e,
        id: e.id ?? `em-${i}`,
        dir: key === 'received' ? 'in' : 'out',
        subject: e.subject || '(no subject)',
        snippet: stripHtml(e.message).slice(0, 140),
        when: key === 'scheduled' && when ? `Scheduled · ${when}` : when || '',
      };
    });
  }, [localData]);

  const counts = useMemo(
    () => ({
      all: mails.length,
      sent: mails.filter((m) => statusKey(m.status) !== 'scheduled').length,
      scheduled: mails.filter((m) => statusKey(m.status) === 'scheduled').length,
    }),
    [mails]
  );

  const rows = mails.filter((m) =>
    filter === 'all' ? true : filter === 'sent' ? statusKey(m.status) !== 'scheduled' : statusKey(m.status) === 'scheduled'
  );

  const TABS = [
    { id: 'all', label: 'All' },
    { id: 'sent', label: 'Sent' },
    { id: 'scheduled', label: 'Scheduled' },
  ];
  const STATS = [
    { value: counts.all, label: 'Total' },
    { value: counts.sent, label: 'Sent', tone: 'var(--gos-green-fg)' },
    { value: counts.scheduled, label: 'Scheduled', tone: 'var(--gos-amber-fg)' },
  ];

  const openReply = (m) =>
    setCompose({
      ...localData,
      toAddress: (m.dir === 'in' ? m.fromAddress : m.toAddress) || localData.email,
      subject: /^re:/i.test(m.subject) ? m.subject : `Re: ${m.subject}`,
      message: '',
    });
  const openForward = (m) =>
    setCompose({
      ...localData,
      toAddress: '',
      subject: /^fwd:/i.test(m.subject) ? m.subject : `Fwd: ${m.subject}`,
      message: m.message || '',
    });
  const openEdit = (m) =>
    setCompose({ ...localData, toAddress: m.toAddress, ccAddress: m.ccAddress, subject: m.subject, message: m.message });
  const openCompose = () => setCompose({ ...localData, subject: '', message: '' });

  return (
    <div className="gos-eml">
      <div className="gos-eml-card">
        <div className="gos-eml-head">
          <div className="gos-eml-title">
            <Ic name="mail" size={16} />
            Emails
            <span className="gos-eml-count">{mails.length}</span>
          </div>
          <button type="button" className="gos-eml-btn soft" onClick={openCompose}>
            <Ic name="send" size={13} />
            Compose
          </button>
        </div>

        <div className="gos-eml-stats">
          {STATS.map((s) => (
            <div className="gos-eml-stat" key={s.label}>
              <div className="gos-eml-stat-val" style={s.tone ? { color: s.tone } : null}>
                {s.value}
              </div>
              <div className="gos-eml-stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="gos-eml-filter">
          <div className="gos-eml-seg">
            {TABS.map((t) => (
              <button type="button" key={t.id} className={filter === t.id ? 'active' : ''} onClick={() => setFilter(t.id)}>
                {t.label}
                <span className="gos-eml-seg-n">{counts[t.id]}</span>
              </button>
            ))}
          </div>
          <button type="button" className="gos-eml-iconbtn" title="Refresh">
            <Ic name="refresh" size={15} />
          </button>
        </div>

        {rows.length ? (
          <div className="gos-eml-list">
            {rows.map((m) => (
              <EmailRow
                key={m.id}
                mail={m}
                open={openId === m.id}
                onToggle={() => setOpenId((id) => (id === m.id ? null : m.id))}
                onReply={openReply}
                onForward={openForward}
                onEdit={openEdit}
              />
            ))}
          </div>
        ) : (
          <div className="gos-eml-empty">
            <span className="gos-eml-empty-ic">
              <Ic name="mail" size={21} />
            </span>
            <div className="gos-eml-empty-t">No {filter} emails</div>
            <div className="gos-eml-empty-s">Emails you send to this record will appear here as a threaded history.</div>
          </div>
        )}
      </div>

      {compose && (
        <div className="gos-eml-compose">
          <ComposeEmail localData={{ ...compose, module: module || compose.module }} module={module} handleClose={() => setCompose(null)} />
        </div>
      )}
    </div>
  );
};

export default Email;
